import React, { useEffect, Fragment, useState } from 'react';
import { Container, Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, number, string, ref, array } from 'yup';
import { format, sub } from 'date-fns';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { makeStyles } from "@material-ui/core/styles";
import { CheckboxInput, RadioInput, TextInput } from '../../../Helpers/Inputs';
import Alert from '@material-ui/lab/Alert';
import BackNext from '../../Common/SubComponents/BackNext';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined';
import NicotineIcon from '../../../../icons/Nicotine.svg';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import FormGroup from '@material-ui/core/FormGroup';
import DUIConvictionForm from './DUIConvictionForm';
import MovingViolationForm from './MovingViolationForm';
import LicenseSuspendedForm from './LicenseSuspendedForm';
import SyringeIcon from '../../../../icons/Syringe.svg';
import PrescriptionsIcon from '../../../../icons/Prescriptions.svg';
import FamilyIcon from '../../../../icons/Family.svg';
import DrivingIcon from '../../../../icons/Driving.svg';
import FinancesIcon from '../../../../icons/Finances.svg';
import AdventuresIcon from '../../../../icons/Adventures.svg';

export default function PersonalHistory(props) {
  const { register, errors, handleSubmit, setValue, triggerValidation, reset } = useForm({
    validationSchema: PersonalHistory.ValidationSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const [bloodPressureOutOfRange, setBloodPressureOutOfRange] = useState(false);

  useEffect(() => {
    if (!props.formState.systolicBloodPressure || props.formState.systolicBloodPressure === '') {
      return;
    }
    if (!props.formState.diastolicBloodPressure || props.formState.diastolicBloodPressure === '') {
      return;
    }

    const systolic = parseInt(props.formState.systolicBloodPressure, 10);
    const diastolic = parseInt(props.formState.diastolicBloodPressure, 10);

    if (systolic > 370 || systolic < 60 || diastolic < 60 || diastolic > 360) {
      setBloodPressureOutOfRange(true);
    } else {
      setBloodPressureOutOfRange(false);
    }
  }, [props.formState.systolicBloodPressure, props.formState.diastolicBloodPressure]);

  const duiForms = props.formState.duiConvictions.map((duiConvictionFormState, index) =>
    <DUIConvictionForm
      formState={duiConvictionFormState}
      onRemove={() => {
        props.onFieldChange(
          'duiConvictions',
          props.formState.duiConvictions.filter((form, formIndex) => formIndex !== index)
        );
        reset();
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'duiConvictions',
          props.formState.duiConvictions.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      setValue={setValue}
      trigger={triggerValidation}
      key={index}
      index={index}
      register={register}
      errors={errors.duiConvictions && errors.duiConvictions[index]}
    />
  );

  const movingViolationForms = props.formState.movingViolationsOrRecklessDrivingConvictions.map((duiConvictionFormState, index) =>
    <MovingViolationForm
      formState={duiConvictionFormState}
      onRemove={() => {
        props.onFieldChange(
          'movingViolationsOrRecklessDrivingConvictions',
          props.formState.movingViolationsOrRecklessDrivingConvictions.filter((form, formIndex) => formIndex !== index)
        );
        reset();
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'movingViolationsOrRecklessDrivingConvictions',
          props.formState.movingViolationsOrRecklessDrivingConvictions.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      setValue={setValue}
      trigger={triggerValidation}
      key={index}
      index={index}
      register={register}
      errors={errors.movingViolationsOrRecklessDrivingConvictions && errors.movingViolationsOrRecklessDrivingConvictions[index]}
    />
  );

  const licenseSuspendedForms = props.formState.licenseSuspendedOrRevokedConvictions.map((convictionFormState, index) =>
    <LicenseSuspendedForm
      formState={convictionFormState}
      onRemove={() => {
        props.onFieldChange(
          'licenseSuspendedOrRevokedConvictions',
          props.formState.licenseSuspendedOrRevokedConvictions.filter((form, formIndex) => formIndex !== index)
        );
        reset();
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'licenseSuspendedOrRevokedConvictions',
          props.formState.licenseSuspendedOrRevokedConvictions.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      setValue={setValue}
      trigger={triggerValidation}
      key={index}
      index={index}
      register={register}
      errors={errors.licenseSuspendedOrRevokedConvictions && errors.licenseSuspendedOrRevokedConvictions[index]}
    />
  );

  useEffect(() => {
    if (props.formState.duiConvictionsCount > props.formState.duiConvictions.length) {
      // add new forms
      var newForms = [];
      for (let i = 0; i < (props.formState.duiConvictionsCount - props.formState.duiConvictions.length); i++) {
        newForms.push({ month: '', year: '' });
      }
      props.onFieldChange('duiConvictions', [...props.formState.duiConvictions, ...newForms]);
    } else if (props.formState.duiConvictionsCount < props.formState.duiConvictions.length) {
      // remove forms
      props.onFieldChange('duiConvictions', [...props.formState.duiConvictions.slice(0, props.formState.duiConvictionsCount)]);
    }
  }, [props.formState.duiConvictionsCount]);

  useEffect(() => {
    if (props.formState.movingViolationsOrRecklessDrivingConvictionsCount > props.formState.movingViolationsOrRecklessDrivingConvictions.length) {
      // add new forms
      var newForms = [];
      for (let i = 0; i < (props.formState.movingViolationsOrRecklessDrivingConvictionsCount - props.formState.movingViolationsOrRecklessDrivingConvictions.length); i++) {
        newForms.push({ month: '', year: '' });
      }
      props.onFieldChange('movingViolationsOrRecklessDrivingConvictions', [...props.formState.movingViolationsOrRecklessDrivingConvictions, ...newForms]);
    } else if (props.formState.movingViolationsOrRecklessDrivingConvictionsCount < props.formState.movingViolationsOrRecklessDrivingConvictions.length) {
      // remove forms
      props.onFieldChange('movingViolationsOrRecklessDrivingConvictions', [...props.formState.movingViolationsOrRecklessDrivingConvictions.slice(0, props.formState.movingViolationsOrRecklessDrivingConvictionsCount)]);
    }
  }, [props.formState.movingViolationsOrRecklessDrivingConvictionsCount]);

  useEffect(() => {
    if (props.formState.licenseSuspendedOrRevokedCount > props.formState.licenseSuspendedOrRevokedConvictions.length) {
      // add new forms
      var newForms = [];
      for (let i = 0; i < (props.formState.licenseSuspendedOrRevokedCount - props.formState.licenseSuspendedOrRevokedConvictions.length); i++) {
        newForms.push({ month: '', year: '' });
      }
      props.onFieldChange('licenseSuspendedOrRevokedConvictions', [...props.formState.licenseSuspendedOrRevokedConvictions, ...newForms]);
    } else if (props.formState.licenseSuspendedOrRevokedCount < props.formState.licenseSuspendedOrRevokedConvictions.length) {
      // remove forms
      props.onFieldChange('licenseSuspendedOrRevokedConvictions', [...props.formState.licenseSuspendedOrRevokedConvictions.slice(0, props.formState.licenseSuspendedOrRevokedCount)]);
    }
  }, [props.formState.licenseSuspendedOrRevokedCount]);

  return (
    <Container>
      <Form onSubmit={handleSubmit(props.onNext)}>
        {props.basicInfo.tobacco === 'true' &&
          <QuestionSection title="Nicotine Use" icon={<NicotineIcon />}>
            <p>I last used a tobacco/nicotine product in</p>
            <Row>
              <Col xs={6}>
                <TextInput
                  name="lastUsedNicotineMonth"
                  id="lastUsedNicotineMonth"
                  aria-label="Month"
                  label="Month"
                  value={props.formState.lastUsedNicotineMonth}
                  onChange={e => props.onFieldChange('lastUsedNicotineMonth', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.lastUsedNicotineMonth}
                />
              </Col>
              <Col xs={6}>
                <TextInput
                  name="lastUsedNicotineYear"
                  id="lastUsedNicotineYear"
                  aria-label="Year"
                  label="Year"
                  value={props.formState.lastUsedNicotineYear}
                  onChange={e => props.onFieldChange('lastUsedNicotineYear', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.lastUsedNicotineYear}
                />
              </Col>
            </Row>

            &nbsp;
            <br />
            <br />

            <p>I use/d:</p>
            <FormGroup>
              <CheckboxInput
                name="cigarettes"
                id="cigarettes"
                checked={props.formState.cigarettes}
                onChange={e => props.onFieldChange('cigarettes', e.target.checked)}
                errors={errors && errors.cigarettes}
                inputRef={register}
                label="Cigarettes"
              />

              {props.formState.cigarettes &&
                <TextInput
                  name="numberOfCigarettesPerDay"
                  id="numberOfCigarettesPerDay"
                  aria-label="# smoked per day (cigarettes)"
                  label="# smoked per day (cigarettes)"
                  value={props.formState.numberOfCigarettesPerDay}
                  onChange={e => props.onFieldChange('numberOfCigarettesPerDay', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.numberOfCigarettesPerDay}
                  fullWidth={false}
                />
              }

              {(props.formState.numberOfCigarettesPerDay && props.formState.numberOfCigarettesPerDay > 50) &&
                <div>Please verify that the number of cigarettes per day has been entered correctly.</div>
              }

              <CheckboxInput
                name="cigars"
                id="cigars"
                checked={props.formState.cigars}
                onChange={e => props.onFieldChange('cigars', e.target.checked)}
                errors={errors && errors.cigars}
                inputRef={register}
                label="Cigars"
              />

              {props.formState.cigars &&
                <Fragment>
                  <TextInput
                    name="numberOfCigarsPerYear"
                    id="numberOfCigarsPerYear"
                    aria-label="# smoked per year (cigars)"
                    label="# smoked per year (cigars)"
                    value={props.formState.numberOfCigarsPerDay}
                    onChange={e => props.onFieldChange('numberOfCigarsPerYear', e.target.value)}
                    inputRef={register}
                    errors={errors && errors.numberOfCigarsPerYear}
                    fullWidth={false}
                  />

                  {(props.formState.numberOfCigarsPerYear && props.formState.numberOfCigarsPerYear > 750) &&
                    <div>Please verify that the number of cigars per year has been entered correctly.</div>
                  }
                  <Alert className="mt-3 mb-3" severity="success" icon={<EmojiObjectsIcon />}>
                    Insider tip: We have handpicked insurance companies that understand the celebratory cigar and will give non-tobacco rates!
                  </Alert>
                </Fragment>
              }

              <CheckboxInput
                name="otherNicotineProducts"
                id="otherNicotineProducts"
                checked={props.formState.otherNicotineProducts}
                onChange={e => props.onFieldChange('otherNicotineProducts', e.target.checked)}
                errors={errors && errors.otherNicotineProducts}
                inputRef={register}
                label="Other Nicotine Products"
              />
            </FormGroup>
          </QuestionSection>
        }

        <QuestionSection title="Prescriptions" icon={<PrescriptionsIcon />}>
          <RadioInput
            name="bloodPressureMedication"
            id="bloodPressureMedication"
            label="I take high blood pressure medication"
            value={props.formState.bloodPressureMedication}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('bloodPressureMedication', e.target.value)}
            inputRef={register}
          />
         
          {props.formState.bloodPressureMedication === "true" &&
            <Fragment>
              <p>Current blood pressure</p>
              <TextInput
                name="systolicBloodPressure"
                id="systolicBloodPressure"
                aria-label="Systolic"
                label="Systolic"
                value={props.formState.systolicBloodPressure}
                onChange={e => props.onFieldChange('systolicBloodPressure', e.target.value)}
                inputRef={register}
                errors={errors && errors.systolicBloodPressure}
                fullWidth={false}
              />
              &nbsp;
              <TextInput
                name="diastolicBloodPressure"
                id="diastolicBloodPressure"
                aria-label="diastolic"
                label="Diastolic"
                value={props.formState.diastolicBloodPressure}
                onChange={e => props.onFieldChange('diastolicBloodPressure', e.target.value)}
                inputRef={register}
                errors={errors && errors.diastolicBloodPressure}
                fullWidth={false}
              />
              {bloodPressureOutOfRange &&
                <div>Please verify that your blood pressure supplied is correct.</div>
              }
              <Alert className="mt-3 mb-3" severity="success" icon={<EmojiObjectsIcon />}>Insider tip: If blood pressure is well controlled, there are insurance companies that will still offer the lowest rates</Alert>
            </Fragment>
          }

          <RadioInput
            name="highCholesterolMedication"
            id="highCholesterolMedication"
            label="I take high cholesterol medication"
            value={props.formState.highCholesterolMedication}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('highCholesterolMedication', e.target.value)}
            inputRef={register}
          />

          {props.formState.highCholesterolMedication === "true" &&
            <Fragment>
              <div>
                <TextInput
                  name="currentCholesterol"
                  id="currentCholesterol"
                  aria-label="Current Cholesterol"
                  label="Current Cholesterol"
                  value={props.formState.currentCholesterol}
                  onChange={e => props.onFieldChange('currentCholesterol', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.currentCholesterol}
                  fullWidth={false}
                />
                {(props.formState.currentCholesterol && (props.formState.currentCholesterol < 100 || props.formState.currentCholesterol > 3165)) &&
                  <div>Please verify that your cholesterol supplied is correct.</div>
                }
              </div>

              <div>
                <TextInput
                  name="hdlRatio"
                  id="hdlRatio"
                  aria-label="hdlRatio"
                  label="HDL Number"
                  value={props.formState.hdlRatio}
                  onChange={e => props.onFieldChange('hdlRatio', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.hdlRatio}
                  fullWidth={false}
                />
                {(props.formState.hdlRatio && (props.formState.hdlRatio < 0 || props.formState.hdlRatio > 100)) &&
                  <div>Please verify that your HDL Number supplied is correct.</div>
                }
              </div>

              <Alert className="mt-3 mb-3" severity="success" icon={<EmojiObjectsIcon />}>Insider tip: If blood pressure is well controlled, there are insurance companies that will still offer the lowest rates.</Alert>
            </Fragment>
          }

          <RadioInput
            name="medicationForDiabetesOrPreDiabetes"
            id="medicationForDiabetesOrPreDiabetes"
            label="I take medication or insulin for diabetes (type 2, type 1), or pre-diabetes"
            value={props.formState.medicationForDiabetesOrPreDiabetes}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('medicationForDiabetesOrPreDiabetes', e.target.value)}
            errors={errors && errors.medicationForDiabetesOrPreDiabetes}
            inputRef={register}
          />

          {props.formState.medicationForDiabetesOrPreDiabetes === "true" &&
            <Fragment>
              <RadioInput
                name="type1Diabetes"
                id="type1Diabetes"
                label="I have type 1 diabetes"
                value={props.formState.type1Diabetes}
                radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => props.onFieldChange('type1Diabetes', e.target.value)}
                errors={errors && errors.type1Diabetes}
                inputRef={register}
              />

              {props.formState.type1Diabetes === "true" &&
                <Fragment>
                  <div>
                    <TextInput
                      name="ageOfDiabetesDiagnosis"
                      id="ageOfDiabetesDiagnosis"
                      aria-label="Age of diagnosis"
                      label="Age of diagnosis"
                      value={props.formState.ageOfDiabetesDiagnosis}
                      onChange={e => props.onFieldChange('ageOfDiabetesDiagnosis', e.target.value)}
                      inputRef={register}
                      errors={errors && errors.ageOfDiabetesDiagnosis}
                      fullWidth={false}
                    />
                  </div>

                  <div>
                    <TextInput
                      name="a1c"
                      id="a1c"
                      aria-label="A1C"
                      label="A1C"
                      value={props.formState.a1c}
                      onChange={e => props.onFieldChange('a1c', e.target.value)}
                      inputRef={register}
                      errors={errors && errors.a1c}
                      fullWidth={false}
                    />
                    {(props.formState.a1c && (props.formState.a1c < 1 || props.formState.a1c > 12)) &&
                      <div>Please verify that your A1C supplied is correct.</div>
                    }
                  </div>
                </Fragment>
              }
            </Fragment>
          }
        </QuestionSection>

        <QuestionSection title="Medical History" icon={<SyringeIcon />}>
          <p>I have been diagnosed with or treated for:</p>
          <FormGroup>
            <CheckboxInput
              name="mildAsthma"
              id="mildAsthma"
              checked={props.formState.mildAsthma}
              onChange={e => props.onFieldChange('mildAsthma', e.target.checked)}
              errors={errors && errors.mildAsthma}
              inputRef={register}
              label="Asthma- Mild/ Moderate"
            />
            <CheckboxInput
              name="severeAsthma"
              id="severeAsthma"
              checked={props.formState.severeAsthma}
              onChange={e => props.onFieldChange('severeAsthma', e.target.checked)}
              errors={errors && errors.severeAsthma}
              inputRef={register}
              label="Asthma- Severe"
            />
            <CheckboxInput
              name="mildAnxietyDepression"
              id="mildAnxietyDepression"
              checked={props.formState.mildAnxietyDepression}
              onChange={e => props.onFieldChange('mildAnxietyDepression', e.target.checked)}
              errors={errors && errors.mildAnxietyDepression}
              inputRef={register}
              label="Anxiety/ Depression- Mild/ Moderate/Situational"
            />
            <CheckboxInput
              name="severeAnxietyDepression"
              id="severeAnxietyDepression"
              checked={props.formState.severeAnxietyDepression}
              onChange={e => props.onFieldChange('severeAnxietyDepression', e.target.checked)}
              errors={errors && errors.severeAnxietyDepression}
              inputRef={register}
              label="Anxiety/ Depression- Severe"
            />
            <CheckboxInput
              name="cardiovascularheartDisease"
              id="cardiovascularheartDisease"
              checked={props.formState.cardiovascularheartDisease}
              onChange={e => props.onFieldChange('cardiovascularheartDisease', e.target.checked)}
              errors={errors && errors.cardiovascularheartDisease}
              inputRef={register}
              label="Cardiovascular/ Heart Disease"
            />
            <CheckboxInput
              name="cancer"
              id="cancer"
              checked={props.formState.cancer}
              onChange={e => props.onFieldChange('cancer', e.target.checked)}
              errors={errors && errors.cancer}
              inputRef={register}
              label="Cancer"
            />
            <CheckboxInput
              name="chronsUlcerativeColitis"
              id="chronsUlcerativeColitis"
              checked={props.formState.chronsUlcerativeColitis}
              onChange={e => props.onFieldChange('chronsUlcerativeColitis', e.target.checked)}
              errors={errors && errors.chronsUlcerativeColitis}
              inputRef={register}
              label="Crohn's/ Ulcerative Colitis"
            />
            <CheckboxInput
              name="copd"
              id="copd"
              checked={props.formState.copd}
              onChange={e => props.onFieldChange('copd', e.target.checked)}
              errors={errors && errors.copd}
              inputRef={register}
              label="COPD"
            />
            <CheckboxInput
              name="hivaids"
              id="hivaids"
              checked={props.formState.hivaids}
              onChange={e => props.onFieldChange('hivaids', e.target.checked)}
              errors={errors && errors.hivaids}
              inputRef={register}
              label="HIV/AIDS"
            />
            <CheckboxInput
              name="kidneyDisease"
              id="kidneyDisease"
              checked={props.formState.kidneyDisease}
              onChange={e => props.onFieldChange('kidneyDisease', e.target.checked)}
              errors={errors && errors.kidneyDisease}
              inputRef={register}
              label="Kidney Disease"
            />
            <RadioInput
              name="hospitalizedInLast24Months"
              id="hospitalizedInLast24Months"
              label="I have been hospitalized in the last 24 months"
              value={props.formState.hospitalizedInLast24Months}
              radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => props.onFieldChange('hospitalizedInLast24Months', e.target.value)}
              errors={errors && errors.hospitalizedInLast24Months}
              inputRef={register}
            />
            <RadioInput
              name="treatedForAlcoholSubstanceAbuse"
              id="treatedForAlcoholSubstanceAbuse"
              label="I have been treated for alcohol or substance abuse"
              value={props.formState.treatedForAlcoholSubstanceAbuse}
              radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
              onChange={e => props.onFieldChange('treatedForAlcoholSubstanceAbuse', e.target.value)}
              errors={errors && errors.treatedForAlcoholSubstanceAbuse}
              inputRef={register}
            />
            {props.formState.treatedForAlcoholSubstanceAbuse === "true" &&
              <Fragment>
                <div className="mb-3">
                  <TextInput
                    name="lastAlcoholSubstanceAbuseTreatmentMonth"
                    id="lastAlcoholSubstanceAbuseTreatmentMonth"
                    aria-label="Month"
                    label="Month"
                    value={props.formState.lastAlcoholSubstanceAbuseTreatmentMonth}
                    onChange={e => props.onFieldChange('lastAlcoholSubstanceAbuseTreatmentMonth', e.target.value)}
                    inputRef={register}
                    errors={errors && errors.lastAlcoholSubstanceAbuseTreatmentMonth}
                    fullWidth={false}
                  />
                  &nbsp;
                  <TextInput
                    name="lastAlcoholSubstanceAbuseTreatmentYear"
                    id="lastAlcoholSubstanceAbuseTreatmentYear"
                    aria-label="Year"
                    label="Year"
                    value={props.formState.lastAlcoholSubstanceAbuseTreatmentYear}
                    onChange={e => props.onFieldChange('lastAlcoholSubstanceAbuseTreatmentYear', e.target.value)}
                    inputRef={register}
                    errors={errors && errors.lastAlcoholSubstanceAbuseTreatmentYear}
                    fullWidth={false}
                  />
                </div>

                {props.formState.lastAlcoholSubstanceAbuseTreatmentYear && ((props.formState.lastAlcoholSubstanceAbuseTreatmentYear < props.born) || (props.formState.lastAlcoholSubstanceAbuseTreatmentYear > 2021)) &&
                  // ---- TODO FIX THIS when the basic info pbi is ready -----
                  <div>Please verify that the date supplied is correct</div>
                }

                <RadioInput
                  name="requireAdditionalAlcoholSubstanceAbuseTreatment"
                  id="requireAdditionalAlcoholSubstanceAbuseTreatment"
                  label="I require additional treatment"
                  value={props.formState.requireAdditionalAlcoholSubstanceAbuseTreatment}
                  radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                  onChange={e => props.onFieldChange('requireAdditionalAlcoholSubstanceAbuseTreatment', e.target.value)}
                  errors={errors && errors.requireAdditionalAlcoholSubstanceAbuseTreatment}
                  inputRef={register}
                />
              </Fragment>
            }
          </FormGroup>
        </QuestionSection>

        <QuestionSection title="Family Medical History" icon={<FamilyIcon />}>
          <RadioInput
            name="siblingOrParentCancerDiagnosedOrDiedBeforeAge70"
            id="siblingOrParentCancerDiagnosedOrDiedBeforeAge70"
            label="I have had a parent or sibling diagnosed with cancer prior to age 70"
            value={props.formState.siblingOrParentCancerDiagnosedOrDiedBeforeAge70}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('siblingOrParentCancerDiagnosedOrDiedBeforeAge70', e.target.value)}
            errors={errors && errors.siblingOrParentCancerDiagnosedOrDiedBeforeAge70}
            inputRef={register}
          />
          {props.formState.siblingOrParentCancerDiagnosedOrDiedBeforeAge70 === "true" &&
            <Fragment>
              <RadioInput
                name="siblingOrParentCancerDiagnosedOrDiedBeforeAge70Relation"
                id="siblingOrParentCancerDiagnosedOrDiedBeforeAge70Relation"
                label="Relation to family member diagnosed"
                value={props.formState.siblingOrParentCancerDiagnosedOrDiedBeforeAge70Relation}
                radios={[{ value: "father", label: "Father" }, { value: "mother", label: "Mother" }, { value: "sibling", label: "Sibling" }]}
                onChange={e => props.onFieldChange('siblingOrParentCancerDiagnosedOrDiedBeforeAge70Relation', e.target.value)}
                errors={errors && errors.siblingOrParentCancerDiagnosedOrDiedBeforeAge70Relation}
                inputRef={register}
              />
              <RadioInput
                name="siblingOrParentCancerDiagnosisBeforeAge70"
                id="siblingOrParentCancerDiagnosisBeforeAge70"
                label="My parent or sibling who was diagnosed or died of cancer prior to age 70 either died or was diagnosed."
                value={props.formState.siblingOrParentCancerDiagnosisBeforeAge70}
                radios={[{ value: "diagnosed", label: "Diagnosed" }, { value: "died", label: "Died" }]}
                onChange={e => props.onFieldChange('siblingOrParentCancerDiagnosisBeforeAge70', e.target.value)}
                errors={errors && errors.siblingOrParentCancerDiagnosisBeforeAge70}
                inputRef={register}
              />

              <Form.Group className="mb-2">
                <TextInput
                  name="siblingOrParentCancerDiagnosisOrDeathAge"
                  id="siblingOrParentCancerDiagnosisOrDeathAge"
                  aria-label="Age of Diagnosis or Death"
                  label={props.formState.siblingOrParentCancerDiagnosisBeforeAge70 == "diagnosed" ? "Age of Diagnosis" : "Age of Death"}
                  value={props.formState.siblingOrParentCancerDiagnosisAge}
                  onChange={e => props.onFieldChange('siblingOrParentCancerDiagnosisOrDeathAge', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.siblingOrParentCancerDiagnosisAge}
                  fullWidth={false}
                />
              </Form.Group>

              <CheckboxInput
                name="additionalRelativeDiagnosedOrDiedFromCancerPriorToAge70"
                id="additionalRelativeDiagnosedOrDiedFromCancerPriorToAge70"
                checked={props.formState.additionalRelativeDiagnosedOrDiedFromCancerPriorToAge70}
                onChange={e => props.onFieldChange('additionalRelativeDiagnosedOrDiedFromCancerPriorToAge70', e.target.checked)}
                errors={errors && errors.additionalRelativeDiagnosedOrDiedFromCancerPriorToAge70}
                inputRef={register}
                label="I have had another relative who was diagnosed or died of cancer prior to age 70."
              />

              {props.formState.additionalRelativeDiagnosedOrDiedFromCancerPriorToAge70 === true &&
                <Fragment>
                  <RadioInput
                    name="additionalRelativeDiagnosedWithCancer"
                    id="additionalRelativeDiagnosedWithCancer"
                    label="The youngest relative who was diagnosed or died at cancer prior to age 70 was:"
                    value={props.formState.additionalRelativeDiagnosedWithCancer}
                    radios={[{ value: "diagnosed", label: "Diagnosed" }, { value: "died", label: "Died" }]}
                    onChange={e => props.onFieldChange('additionalRelativeDiagnosedWithCancer', e.target.value)}
                    errors={errors && errors.additionalRelativeDiagnosedWithCancer}
                    inputRef={register}
                  />
                  {(props.formState.additionalRelativeDiagnosedWithCancer === "diagnosed" || props.formState.additionalRelativeDiagnosedWithCancer === "died") &&
                    <div className="mb-2">
                      <TextInput
                        name="additionalRelativeCancerDiagnosisOrDiedAge"
                        id="additionalRelativeCancerDiagnosisOrDiedAge"
                        aria-label={props.formState.additionalRelativeDiagnosedWithCancer === "diagnosed" ? "Age of Diagnosis" : "Age of Death"}
                        label={props.formState.additionalRelativeDiagnosedWithCancer === "diagnosed" ? "Age of Diagnosis" : "Age of Death"}
                        value={props.formState.additionalRelativeCancerDiagnosisOrDiedAge}
                        onChange={e => props.onFieldChange('additionalRelativeCancerDiagnosisOrDiedAge', e.target.value)}
                        inputRef={register}
                        errors={errors && errors.additionalRelativeCancerDiagnosisOrDiedAge}
                        fullWidth={false}
                      />
                    </div>
                  }
                </Fragment>
              }
            </Fragment>
          }

          <RadioInput
            name="siblingOrParentHeartDiseaseBeforeAge70"
            id="siblingOrParentHeartDiseaseBeforeAge70"
            label="I have had a parent or sibling diagnosed with cardiovascular/heart disease prior to age 70"
            value={props.formState.siblingOrParentHeartDiseaseBeforeAge70}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('siblingOrParentHeartDiseaseBeforeAge70', e.target.value)}
            errors={errors && errors.siblingOrParentHeartDiseaseBeforeAge70}
            inputRef={register}
          />
        </QuestionSection>

        <QuestionSection title="Driving Record" icon={<DrivingIcon />}>
          <RadioInput
            name="convictedOfDUIDWI"
            id="convictedOfDUIDWI"
            label="I have been convicted of a DUI/ DWI"
            value={props.formState.convictedOfDUIDWI}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('convictedOfDUIDWI', e.target.value)}
            errors={errors && errors.convictedOfDUIDWI}
            inputRef={register}
          />
          {props.formState.convictedOfDUIDWI === "true" &&
            <Fragment>
              <TextInput
                name="duiConvictionsCount"
                id="duiConvictionsCount"
                aria-label="Number of convictions"
                label="Number of convictions"
                value={props.formState.duiConvictionsCount}
                onChange={e => props.onFieldChange('duiConvictionsCount', e.target.value)}
                inputRef={register}
                errors={errors && errors.duiConvictionsCount}
                fullWidth={false}
              />
              <div className="mb-3">{duiForms}</div>
            </Fragment>
          }
          <RadioInput
            name="movingViolationsOrRecklessDrivingInLast5years"
            id="movingViolationsOrRecklessDrivingInLast5years"
            label="I have had moving violations or reckless driving convictions in the last 5 years"
            value={props.formState.movingViolationsOrRecklessDrivingInLast5years}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('movingViolationsOrRecklessDrivingInLast5years', e.target.value)}
            errors={errors && errors.movingViolationsOrRecklessDrivingInLast5years}
            inputRef={register}
          />
          {props.formState.movingViolationsOrRecklessDrivingInLast5years === "true" &&
            <Fragment>
              <TextInput
                name="movingViolationsOrRecklessDrivingConvictionsCount"
                id="movingViolationsOrRecklessDrivingConvictionsCount"
                className="mb-3"
                aria-label="movingViolationsOrRecklessDrivingConvictionsCount"
                label="Number of convictions in Past 5 Years"
                value={props.formState.movingViolationsOrRecklessDrivingConvictionsCount}
                onChange={e => props.onFieldChange('movingViolationsOrRecklessDrivingConvictionsCount', e.target.value)}
                inputRef={register}
                errors={errors && errors.movingViolationsOrRecklessDrivingConvictionsCount}
                fullWidth={false}
              />
              {props.formState.movingViolationsOrRecklessDrivingConvictionsCount > 0 &&
                <div className="mb-3 mt-3">
                  {movingViolationForms}
                </div>
              }
            </Fragment>
          }
          <RadioInput
            name="licenseSuspendedOrRevoked"
            id="licenseSuspendedOrRevoked"
            label="I have had my license suspended or revoked"
            value={props.formState.licenseSuspendedOrRevoked}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('licenseSuspendedOrRevoked', e.target.value)}
            errors={errors && errors.licenseSuspendedOrRevoked}
            inputRef={register}
          />
          {props.formState.licenseSuspendedOrRevoked === "true" &&
            <Fragment>
              <TextInput
                name="licenseSuspendedOrRevokedCount"
                id="licenseSuspendedOrRevokedCount"
                aria-label="Number of times"
                label="Number of times"
                value={props.formState.licenseSuspendedOrRevokedCount}
                onChange={e => props.onFieldChange('licenseSuspendedOrRevokedCount', e.target.value)}
                inputRef={register}
                errors={errors && errors.licenseSuspendedOrRevokedCount}
                fullWidth={false}
              />
              {props.formState.licenseSuspendedOrRevokedCount > 0 &&
                <div className="mb-3 mt-3">
                  {licenseSuspendedForms}
                </div>
              }
            </Fragment>
          }
        </QuestionSection>

        <QuestionSection title="Finances" icon={<FinancesIcon />}>
          <RadioInput
            name="receivingSocialSecurityOrDisabled"
            id="receivingSocialSecurityOrDisabled"
            label="I am receiving social security or disabled"
            value={props.formState.receivingSocialSecurityOrDisabled}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('receivingSocialSecurityOrDisabled', e.target.value)}
            errors={errors && errors.receivingSocialSecurityOrDisabled}
            inputRef={register}
          />
          <RadioInput
            name="bankruptcyInTheLast2Years"
            id="bankruptcyInTheLast2Years"
            label="I have had a bankruptcy in the last 2 years"
            value={props.formState.bankruptcyInTheLast2Years}
            radios={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            onChange={e => props.onFieldChange('bankruptcyInTheLast2Years', e.target.value)}
            errors={errors && errors.bankruptcyInTheLast2Years}
            inputRef={register}
          />
        </QuestionSection>

        <QuestionSection title="Adventures" icon={<AdventuresIcon />}>
          <p>I have participated in the following:</p>
          <FormGroup className="checkboxes">

            <CheckboxInput
              name="automobileOrMotorcycleRacing"
              id="automobileOrMotorcycleRacing"
              checked={props.formState.automobileOrMotorcycleRacing}
              onChange={e => props.onFieldChange('automobileOrMotorcycleRacing', e.target.checked)}
              errors={errors && errors.automobileOrMotorcycleRacing}
              inputRef={register}
              label="Automobile or Motorcycle Racing"
            />
            <CheckboxInput
              name="ballooning"
              id="ballooning"
              checked={props.formState.ballooning}
              onChange={e => props.onFieldChange('ballooning', e.target.checked)}
              errors={errors && errors.ballooning}
              inputRef={register}
              label="Ballooning"
            />
            <CheckboxInput
              name="bungeeJumping"
              id="bungeeJumping"
              checked={props.formState.bungeeJumping}
              onChange={e => props.onFieldChange('bungeeJumping', e.target.checked)}
              errors={errors && errors.bungeeJumping}
              inputRef={register}
              label="Bungee Jumping"
            />
            <CheckboxInput
              name="hangGliding"
              id="hangGliding"
              checked={props.formState.hangGliding}
              onChange={e => props.onFieldChange('hangGliding', e.target.checked)}
              errors={errors && errors.hangGliding}
              inputRef={register}
              label="Hang Gliding"
            />
            <CheckboxInput
              name="pilotingAnAircraft"
              id="pilotingAnAircraft"
              checked={props.formState.pilotingAnAircraft}
              onChange={e => props.onFieldChange('pilotingAnAircraft', e.target.checked)}
              errors={errors && errors.pilotingAnAircraft}
              inputRef={register}
              label="Piloting an aircraft"
            />
            {props.formState.pilotingAnAircraft &&
              <Fragment>
                <RadioInput
                  name="pilotType"
                  id="pilotType"
                  label="I am a"
                  value={props.formState.pilotType}
                  radios={[{ value: "0", label: "Commercial" }, { value: "1", label: "Military" }, { value: "2", label: "Private" }, { value: "3", label: "Commercial and Private" }]}
                  onChange={e => props.onFieldChange('pilotType', e.target.value)}
                  errors={errors && errors.pilotType}
                  inputRef={register}
                />
                <TextInput
                  name="pilotingHoursPerYear"
                  id="pilotingHoursPerYear"
                  aria-label="Hours per year"
                  label="Hours per year"
                  value={props.formState.pilotingHoursPerYear}
                  onChange={e => props.onFieldChange('pilotingHoursPerYear', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.pilotingHoursPerYear}
                  fullWidth={false}
                />
              </Fragment>
            }
            <CheckboxInput
              name="scubaDiving"
              id="scubaDiving"
              checked={props.formState.scubaDiving}
              onChange={e => props.onFieldChange('scubaDiving', e.target.checked)}
              errors={errors && errors.scubaDiving}
              inputRef={register}
              label="Scuba Diving"
            />
            {props.formState.scubaDiving &&
              <div>
                <TextInput
                  name="scubaDivesPerYear"
                  id="scubaDivesPerYear"
                  aria-label="Dives per year"
                  label="Dives per year"
                  value={props.formState.scubaDivesPerYear}
                  onChange={e => props.onFieldChange('scubaDivesPerYear', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.scubaDivesPerYear}
                  fullWidth={false}
                />
                &nbsp;
                <TextInput
                  name="scubaDeepestDiveInFeet"
                  id="scubaDeepestDiveInFeet"
                  aria-label="Deepest dive (feet)"
                  label="Deepest dive (feet)"
                  value={props.formState.scubaDeepestDiveInFeet}
                  onChange={e => props.onFieldChange('scubaDeepestDiveInFeet', e.target.value)}
                  inputRef={register}
                  errors={errors && errors.scubaDeepestDiveInFeet}
                  fullWidth={false}
                />
              </div>
            }
            <CheckboxInput
              name="skyDiving"
              id="skyDiving"
              checked={props.formState.skyDiving}
              onChange={e => props.onFieldChange('skyDiving', e.target.checked)}
              errors={errors && errors.skyDiving}
              inputRef={register}
              label="Sky Diving"
            />
          </FormGroup>
        </QuestionSection>

        <Row className="justify-content-center m-5">
          <BackNext onBack={props.onBack} loading={props.isSubmitting === true} />
        </Row>
      </Form>
    </Container >
  );
}

const QuestionSection = (props) => {
  const useStyles = makeStyles(theme => ({
    questionBox: {
      backgroundColor: 'white',
      borderRadius: '6px',
      boxShadow: '0px 1px 3px #00000033',
      border: '2px solid #ccc',
      padding: '1rem',
    },
    sectionTitle: {
      fontSize: '24px',
      fontWeight: '500',
    }
  }));

  const classes = useStyles();
  return (
    <Row className="justify-content-center pt-3">
      <Col sm={12} lg={6}>
        <div className={`${classes.questionBox}`}>
          <p className="text-center">{props.icon}</p>
          <p className={`text-center ${classes.sectionTitle}`}>{props.title}</p>
          {props.children}
        </div>
      </Col>

    </Row>
  )


}

PersonalHistory.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

PersonalHistory.ValidationSchema = object().shape({
  lastUsedNicotineMonth: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a month between 1 and 12')
    .min(1, 'Must be a month between 1 and 12')
    .max(12, 'Must be a month between 1 and 12'),
  lastUsedNicotineYear: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a four digit year')
    .max(format(new Date(), 'yyyy'), 'Must be a four digit year.')
    .min(format(sub(new Date(), { years: 100 }), 'yyyy'), 'Must be a four digit year.'),
  cigarettes: string(),
  numberOfCigarettesPerDay: number()
    .transform(emptyStringToUndefined)
    .typeError('# smoked per day (cigarettes)')
    .min(1, '# smoked per day (cigarettes)'),
  cigars: string(),
  numberOfCigarsPerYear: number()
    .transform(emptyStringToUndefined)
    .typeError('# smoked per year (cigars)')
    .min(1, '# smoked per year (cigars)'),
  otherNicotineProducts: string(),
  bloodPressureMedication: string(),
  systolicBloodPressure: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(1, 'Must be a number'),
  diastolicBloodPressure: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(1, 'Must be a number'),
  highCholesterolMedication: string(),
  currentCholesterol: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(1, 'Must be a number'),
  hdlRatio: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
  medicationForDiabetesOrPreDiabetes: string(),
  type1Diabetes: string(),
  ageOfDiabetesDiagnosis: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
  a1c: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),

  mildAsthma: string(),
  severeAsthma: string(),
  mildAnxietyDepression: string(),
  severeAnxietyDepression: string(),
  cardiovascularheartDisease: string(),
  cancer: string(),
  chronsUlcerativeColitis: string(),
  copd: string(),
  hivaids: string(),
  kidneyDisease: string(),
  hospitalizedInLast24Months: string(),

  treatedForAlcoholSubstanceAbuse: string(),
  lastAlcoholSubstanceAbuseTreatmentMonth: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a month between 1 and 12')
    .min(1, 'Must be a month between 1 and 12')
    .max(12, 'Must be a month between 1 and 12'),
  lastAlcoholSubstanceAbuseTreatmentYear: string()
    .matches(/^\d{4}$/, 'Must be a four digit year'),
  requireAdditionalAlcoholSubstanceAbuseTreatment: string(),

  convictedOfDUIDWI: string(),
  duiConvictionsCount: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
  duiConvictions: array(object().shape({
    month: number()
      .transform(emptyStringToUndefined)
      .typeError('Must be a month between 1 and 12')
      .min(1, 'Must be a month between 1 and 12')
      .max(12, 'Must be a month between 1 and 12'),
    year: number()
      .transform(emptyStringToUndefined)
      .typeError('Must be a four digit year')
      .max(format(new Date(), 'yyyy'), 'Must be a four digit year.')
      .min(format(sub(new Date(), { years: 100 }), 'yyyy'), 'Must be a four digit year.'),
  })),

  movingViolationsOrRecklessDrivingInLast5years: string(),
  movingViolationsOrRecklessDrivingConvictionsCount: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
  movingViolationsOrRecklessDrivingConvictions: array(object().shape({
    month: number()
      .transform(emptyStringToUndefined)
      .typeError('Must be a month between 1 and 12')
      .min(1, 'Must be a month between 1 and 12')
      .max(12, 'Must be a month between 1 and 12'),
    year: number()
      .transform(emptyStringToUndefined)
      .typeError('Must be a four digit year')
      .max(format(new Date(), 'yyyy'), 'Must be a four digit year.')
      .min(format(sub(new Date(), { years: 100 }), 'yyyy'), 'Must be a four digit year.'),
  })),

  licenseSuspendedOrRevoked: string(),
  licenseSuspendedOrRevokedCount: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
  licenseSuspendedOrRevokedConvictions: array(object().shape({
    month: number()
      .transform(emptyStringToUndefined)
      .typeError('Must be a month between 1 and 12')
      .min(1, 'Must be a month between 1 and 12')
      .max(12, 'Must be a month between 1 and 12'),
    year: number()
      .transform(emptyStringToUndefined)
      .typeError('Must be a four digit year')
      .max(format(new Date(), 'yyyy'), 'Must be a four digit year.')
      .min(format(sub(new Date(), { years: 100 }), 'yyyy'), 'Must be a four digit year.'),
  })),

  siblingOrParentCancerDiagnosisOrDeathAge: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),

  pilotingHoursPerYear: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
  scubaDivesPerYear: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
  scubaDeepestDiveInFeet: number()
    .transform(emptyStringToUndefined)
    .typeError('Must be a number')
    .min(0, 'Must be a number'),
});

PersonalHistory.DefaultFormState = (prefill = false) => {
  return {
    lastUsedNicotineMonth: prefill ? '3' : '',
    lastUsedNicotineYear: prefill ? '2019' : '',

    cigarettes: prefill ? true : false,
    numberOfCigarettesPerDay: prefill ? '10' : '',
    cigars: prefill ? true : false,
    numberOfCigarsPerYear: prefill ? '6' : '',
    otherNicotineProducts: prefill ? true : false,

    bloodPressureMedication: prefill ? 'true' : 'false',
    systolicBloodPressure: prefill ? '120' : '',
    diastolicBloodPressure: prefill ? '80' : '',

    highCholesterolMedication: prefill ? 'true' : 'false',
    currentCholesterol: prefill ? '300' : '',
    hdlRatio: prefill ? '50' : '',

    medicationForDiabetesOrPreDiabetes: prefill ? 'true' : 'false',
    type1Diabetes: prefill ? 'true' : 'false',
    ageOfDiabetesDiagnosis: prefill ? '45' : '',
    a1c: prefill ? '5' : '',

    mildAsthma: false,
    severeAsthma: false,
    mildAnxietyDepression: false,
    severeAnxietyDepression: false,
    cardiovascularheartDisease: false,
    cancer: false,
    chronsUlcerativeColitis: false,
    copd: false,
    hivaids: false,
    kidneyDisease: false,
    hospitalizedInLast24Months: 'false',

    treatedForAlcoholSubstanceAbuse: 'false',
    lastAlcoholSubstanceAbuseTreatmentMonth: '',
    lastAlcoholSubstanceAbuseTreatmentYear: '',
    requireAdditionalAlcoholSubstanceAbuseTreatment: 'false',

    siblingOrParentCancerDiagnosedOrDiedBeforeAge70: 'false',
    siblingOrParentCancerDiagnosedOrDiedBeforeAge70Relation: '',
    siblingOrParentCancerDiagnosisBeforeAge70: 'false',
    siblingOrParentCancerDiagnosisOrDeathAge: '',
    siblingOrParentHeartDiseaseBeforeAge70: 'false',
    additionalRelativeDiagnosedOrDiedFromCancerPriorToAge70: false,
    additionalRelativeDiagnosedWithCancer: '',
    additionalRelativeCancerDiagnosisOrDiedAge: '',

    convictedOfDUIDWI: 'false',
    duiConvictionsCount: '',
    duiConvictions: [],

    movingViolationsOrRecklessDrivingInLast5years: 'false',
    movingViolationsOrRecklessDrivingConvictionsCount: '',
    movingViolationsOrRecklessDrivingConvictions: [],

    licenseSuspendedOrRevoked: 'false',
    licenseSuspendedOrRevokedCount: '',
    licenseSuspendedOrRevokedConvictions: [],

    receivingSocialSecurityOrDisabled: 'false',
    bankruptcyInTheLast2Years: 'false',

    automobileOrMotorcycleRacing: false,
    ballooning: false,
    bungeeJumping: false,
    hangGliding: false,
    pilotingAnAircraft: false,
    pilotType: '',
    pilotingHoursPerYear: '',
    scubaDiving: false,
    scubaDivesPerYear: '',
    scubaDeepestDiveInFeet: '',
    skyDiving: false,
  };
}


// WEBPACK FOOTER //
// ./src/components/Quoters/PreQuote/steps/PersonalHistory.js